import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserRoles, setUserRole, deleteUserRole } from '../../hooks/useFirestore';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ENV } from '../../collections';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatCard from '../../components/ui/StatCard';
import AnimatedList from '../../components/ui/AnimatedList';
import { SkeletonGrid } from '../../components/ui/Skeleton';
import { cn } from '../../utils/cn';
import BulkUserImportModal from './BulkUserImportModal';

const ROLE_BADGES = {
  admin: { label: 'Admin', color: 'bg-red-50 text-red-700', ring: 'ring-red-200' },
  communicator: { label: 'Communicator', color: 'bg-blue-50 text-blue-700', ring: 'ring-blue-200' },
  peoplePartner: { label: 'People Partner', color: 'bg-green-50 text-green-700', ring: 'ring-green-200' },
};

function RoleBadge({ role }) {
  const config = ROLE_BADGES[role];
  if (!config) return null;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset', config.color, config.ring)}>
      {config.label}
    </span>
  );
}

export default function UserManagement() {
  const { roles, loading } = useUserRoles();
  const { user } = useAuth();
  const toast = useToast();

  // Add user modal
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [newIsCommunicator, setNewIsCommunicator] = useState(false);
  const [newIsPeoplePartner, setNewIsPeoplePartner] = useState(false);
  const [newPPAssignedEmails, setNewPPAssignedEmails] = useState([]);
  const [newPPEmailInput, setNewPPEmailInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editTarget, setEditTarget] = useState(null); // { email, isAdmin, isCommunicator }
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editIsCommunicator, setEditIsCommunicator] = useState(false);
  const [editIsPeoplePartner, setEditIsPeoplePartner] = useState(false);
  const [editPPAssignedEmails, setEditPPAssignedEmails] = useState([]);
  const [editPPEmailInput, setEditPPEmailInput] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);

  const admins = roles.filter((r) => r.isAdmin);
  const communicators = roles.filter((r) => r.isCommunicator && !r.isAdmin);
  const peoplePartners = roles.filter((r) => r.isPeoplePartner && !r.isAdmin);

  const isSelf = (email) => user?.email === email;

  /* ── Add user ── */
  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (!newIsAdmin && !newIsCommunicator && !newIsPeoplePartner) {
      toast.error('Select at least one role');
      return;
    }
    setSaving(true);
    try {
      await setUserRole(email, {
        isAdmin: newIsAdmin,
        isCommunicator: newIsCommunicator,
        isPeoplePartner: newIsPeoplePartner,
        ppAssignedEmails: newPPAssignedEmails,
      });
      toast.success(`${email} added`);
      setShowAdd(false);
      setNewEmail('');
      setNewIsAdmin(false);
      setNewIsCommunicator(false);
      setNewIsPeoplePartner(false);
      setNewPPAssignedEmails([]);
      setNewPPEmailInput('');
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to add user');
    }
    setSaving(false);
  };

  /* ── Edit user ── */
  const openEdit = (r) => {
    setEditTarget(r);
    setEditIsAdmin(!!r.isAdmin);
    setEditIsCommunicator(!!r.isCommunicator);
    setEditIsPeoplePartner(!!r.isPeoplePartner);
    setEditPPAssignedEmails(r.ppAssignedEmails || []);
    setEditPPEmailInput('');
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    if (!editIsAdmin && !editIsCommunicator && !editIsPeoplePartner) {
      toast.error('Select at least one role, or remove the user instead');
      return;
    }
    if (isSelf(editTarget.email) && !editIsAdmin) {
      toast.error("You can't remove your own admin role");
      return;
    }
    setEditSaving(true);
    try {
      await setUserRole(editTarget.email, {
        isAdmin: editIsAdmin,
        isCommunicator: editIsCommunicator,
        isPeoplePartner: editIsPeoplePartner,
        ppAssignedEmails: editPPAssignedEmails,
      });
      toast.success(`${editTarget.email} updated`);
      setEditTarget(null);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to update user');
    }
    setEditSaving(false);
  };

  /* ── Delete user ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (isSelf(deleteTarget.email)) {
      toast.error("You can't remove yourself");
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteUserRole(deleteTarget.email);
      toast.success(`${deleteTarget.email} removed`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to remove user');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-8">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-end justify-between gap-4"
      >
        <div>
          <h1 className="text-display-sm text-navy-900">User Management</h1>
          <p className="text-navy-400 mt-1">
            Manage admins and communicators for the <span className="font-semibold text-navy-600">{ENV}</span> environment.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => setShowBulkImport(true)} size="lg">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Bulk Import
          </Button>
          <Button onClick={() => setShowAdd(true)} size="lg">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
            </svg>
            Add User
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      {!loading && roles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={roles.length} icon="👥" index={0} />
          <StatCard label="Admins" value={admins.length} icon="🔑" index={1} />
          <StatCard label="Communicators" value={communicators.length} icon="📨" index={2} />
          <StatCard label="People Partners" value={peoplePartners.length} icon="🤝" index={3} />
        </div>
      )}

      {/* User list */}
      {loading ? (
        <SkeletonGrid count={4} />
      ) : roles.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-24 text-center"
        >
          <div className="w-20 h-20 rounded-3xl bg-surface-100 flex items-center justify-center text-4xl mb-6">👤</div>
          <h3 className="text-xl font-bold text-navy-900 mb-2">No users configured</h3>
          <p className="text-navy-400 mb-6 max-w-sm">Add your first admin or communicator to get started.</p>
          <Button onClick={() => setShowAdd(true)}>Add First User</Button>
        </motion.div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50/50">
                  <th className="text-left px-6 py-3 font-semibold text-navy-500 uppercase tracking-wider text-xs">Email</th>
                  <th className="text-left px-6 py-3 font-semibold text-navy-500 uppercase tracking-wider text-xs">Roles</th>
                  <th className="text-right px-6 py-3 font-semibold text-navy-500 uppercase tracking-wider text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {roles.map((r) => (
                    <motion.tr
                      key={r.email}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-surface-100 last:border-0 hover:bg-surface-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {r.email[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-navy-900 truncate">{r.email}</p>
                            {isSelf(r.email) && (
                              <span className="text-xs text-navy-400">(you)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.isAdmin && <RoleBadge role="admin" />}
                          {r.isCommunicator && <RoleBadge role="communicator" />}
                          {r.isPeoplePartner && <RoleBadge role="peoplePartner" />}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(r)}
                            className="p-1.5 rounded-lg hover:bg-surface-100 text-navy-400 hover:text-navy-600 transition-colors"
                            title="Edit roles"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                          </button>
                          <button
                            onClick={() => !isSelf(r.email) && setDeleteTarget(r)}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              isSelf(r.email)
                                ? 'text-navy-200 cursor-not-allowed'
                                : 'hover:bg-red-50 text-navy-400 hover:text-red-500',
                            )}
                            title={isSelf(r.email) ? "Can't remove yourself" : 'Remove user'}
                            disabled={isSelf(r.email)}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Add User Modal ── */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add User" size="sm">
        <div className="space-y-5">
          <Input
            label="Email Address"
            placeholder="name@forter.com"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoFocus
          />

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-navy-500 uppercase tracking-wider">Roles</label>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newIsAdmin}
                  onChange={(e) => setNewIsAdmin(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                />
                <div>
                  <p className="text-sm font-medium text-navy-800">Admin</p>
                  <p className="text-xs text-navy-400">Full access to manage cycles, compensations, and users</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newIsCommunicator}
                  onChange={(e) => setNewIsCommunicator(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                />
                <div>
                  <p className="text-sm font-medium text-navy-800">Communicator</p>
                  <p className="text-xs text-navy-400">Can view and present assigned compensation statements</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newIsPeoplePartner}
                  onChange={(e) => setNewIsPeoplePartner(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                />
                <div>
                  <p className="text-sm font-medium text-navy-800">People Partner</p>
                  <p className="text-xs text-navy-400">Can view as assigned communicators to support their teams</p>
                </div>
              </label>
            </div>
          </div>

          {/* PP Assigned Communicators */}
          {newIsPeoplePartner && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-navy-500 uppercase tracking-wider">Assigned Communicators</label>
              <p className="text-xs text-navy-400">Emails this People Partner can view as.</p>
              {newPPAssignedEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {newPPAssignedEmails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200">
                      {e}
                      <button onClick={() => setNewPPAssignedEmails((prev) => prev.filter((x) => x !== e))} className="p-0.5 rounded-full hover:bg-green-200 transition-colors">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="communicator@forter.com"
                  type="email"
                  value={newPPEmailInput}
                  onChange={(e) => setNewPPEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = newPPEmailInput.trim().toLowerCase();
                      if (val && val.includes('@') && !newPPAssignedEmails.includes(val)) {
                        setNewPPAssignedEmails((prev) => [...prev, val]);
                        setNewPPEmailInput('');
                      }
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const val = newPPEmailInput.trim().toLowerCase();
                    if (val && val.includes('@') && !newPPAssignedEmails.includes(val)) {
                      setNewPPAssignedEmails((prev) => [...prev, val]);
                      setNewPPEmailInput('');
                    }
                  }}
                >Add</Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} loading={saving} disabled={!newEmail.trim()}>Add User</Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit User Modal ── */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Edit User Roles" size="sm">
        {editTarget && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 p-3 bg-surface-50 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-400 to-cyan-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {editTarget.email[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold text-navy-900">{editTarget.email}</p>
                {isSelf(editTarget.email) && <p className="text-xs text-navy-400">(you)</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-navy-500 uppercase tracking-wider">Roles</label>
              <div className="flex flex-col gap-3">
                <label className={cn(
                  'flex items-center gap-3 select-none',
                  isSelf(editTarget.email) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}>
                  <input
                    type="checkbox"
                    checked={editIsAdmin}
                    onChange={(e) => setEditIsAdmin(e.target.checked)}
                    disabled={isSelf(editTarget.email)}
                    className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-navy-800">Admin</p>
                    {isSelf(editTarget.email) && (
                      <p className="text-xs text-amber-600">You cannot remove your own admin role</p>
                    )}
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editIsCommunicator}
                    onChange={(e) => setEditIsCommunicator(e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-navy-800">Communicator</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editIsPeoplePartner}
                    onChange={(e) => setEditIsPeoplePartner(e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-accent-600 focus:ring-accent-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-navy-800">People Partner</p>
                    <p className="text-xs text-navy-400">Can view as assigned communicators</p>
                  </div>
                </label>
              </div>
            </div>

            {/* PP Assigned Communicators */}
            {editIsPeoplePartner && (
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-navy-500 uppercase tracking-wider">Assigned Communicators</label>
                <p className="text-xs text-navy-400">Emails this People Partner can view as.</p>
                {editPPAssignedEmails.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {editPPAssignedEmails.map((e) => (
                      <span key={e} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200">
                        {e}
                        <button onClick={() => setEditPPAssignedEmails((prev) => prev.filter((x) => x !== e))} className="p-0.5 rounded-full hover:bg-green-200 transition-colors">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    placeholder="communicator@forter.com"
                    type="email"
                    value={editPPEmailInput}
                    onChange={(e) => setEditPPEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = editPPEmailInput.trim().toLowerCase();
                        if (val && val.includes('@') && !editPPAssignedEmails.includes(val)) {
                          setEditPPAssignedEmails((prev) => [...prev, val]);
                          setEditPPEmailInput('');
                        }
                      }
                    }}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const val = editPPEmailInput.trim().toLowerCase();
                      if (val && val.includes('@') && !editPPAssignedEmails.includes(val)) {
                        setEditPPAssignedEmails((prev) => [...prev, val]);
                        setEditPPEmailInput('');
                      }
                    }}
                  >Add</Button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button onClick={handleEdit} loading={editSaving}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <BulkUserImportModal open={showBulkImport} onClose={() => setShowBulkImport(false)} />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove User"
        message={`Are you sure you want to remove "${deleteTarget?.email}"? They will lose all admin and communicator access.`}
        confirmLabel="Remove"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
